import { AspectRatio, Box, ContainerProps } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  FeedPost,
  FeedPostBody,
  FeedPostButtonWrapper,
  FeedPostBuyButton,
  FeedPostCenteredButtons,
  FeedPostDataText,
  FeedPostDate,
  FeedPostHeader,
  FeedPostHeaderDataRow,
  FeedPostHeaderStack,
  FeedPostMenu,
  FeedPostModelImage,
  FeedPostModelName,
  FeedPostPin,
} from '../../../../../components/shared/FeedPost/FeedPost';
import { ScheduledForDateTimeBatch } from '../../../../../components/shared/ScheduledForDateTimeBatch/ScheduledForDateTimeBatch';
import {
  FeedPhotoPostFragment,
  PostStatusEnum,
  PostTypeEnum,
} from '../../../../../generated/feed';
import { useActiveApiLanguage } from '../../../../../hooks/useActiveApiLanguage';
import { useFeedPostUploadProgress } from '../../../hooks/useFeedPostUploadProgress';
import { PostFunctionProps } from '../../../posts';
import { avatarURL, typeDependantModelName } from '../../../utils';
import { PostFooter } from './PostFooter';
import { FeedPostBrokenInlineAlert } from './PostInlineAlert';
import { TempFeedPostScheduleProp } from './types';

export const PhotoPostBaseStructure: React.FC<
  {
    containerProps?: ContainerProps;
    children?: React.ReactNode;
    tags: string[];
    formattedPrice: string;
    isPostOwnedByUser: boolean;
  } & TempFeedPostScheduleProp &
    FeedPhotoPostFragment &
    PostFunctionProps
> = ({
  id,
  status,
  rejected,
  deRejection,
  enRejection,
  model,
  created,
  pinned,
  price,
  formattedPrice,
  title,
  text,
  likes,
  comments,
  tips,
  purchases,
  type,
  photos,
  tags,
  isPublished,
  publicationScheduledFor,
  tippingGoal,
  onClickOnPin,
  onClickOnLikes,
  onClickOnTips,
  onClickOnEdit,
  onClickOnDelete,
  children,
  containerProps,
  isPostOwnedByUser,
}) => {
  const { t } = useTranslation(['feed']);
  const isFeaturedPost = type === PostTypeEnum.FpPhotoGallery;
  const uploadProgressElement = useFeedPostUploadProgress({
    status,
    postId: id,
  });

  const apiLang = useActiveApiLanguage();
  const rejectionReason = React.useMemo(() => {
    switch (apiLang) {
      case 'de':
        return deRejection;
      default:
        return enRejection;
    }
  }, [deRejection, enRejection, apiLang]);

  const isPinnable = status !== PostStatusEnum.Broken;
  const isEditable = status !== PostStatusEnum.Broken;
  const isScheduled =
    status === PostStatusEnum.Ok && !isPublished && !!publicationScheduledFor;

  return (
    <FeedPost
      postId={id}
      status={status}
      rejectionReason={rejectionReason}
      isFeatured={isFeaturedPost}
      isScheduled={isScheduled}
      onDeletePost={() => onClickOnDelete(id)}
      {...containerProps}
    >
      <FeedPostHeader borderRadius={isFeaturedPost ? '8px 8px 0 0' : '0'}>
        <FeedPostModelImage
          src={avatarURL(isFeaturedPost, model.avatar?.url)}
          srcSet={isFeaturedPost ? undefined : model.avatar?.srcSet}
        />
        <FeedPostHeaderStack>
          <FeedPostModelName
            children={typeDependantModelName(
              isFeaturedPost,
              model.name,
              t('feed:label.FeaturedPostAvatar')
            )}
          />
          <FeedPostHeaderDataRow>
            {isScheduled ? (
              <ScheduledForDateTimeBatch dateTime={publicationScheduledFor} />
            ) : (
              <>
                <FeedPostDate children={created} />
                {(photos?.length ?? 0) > 1 && (
                  <FeedPostDataText
                    children={t('feed:text.Bilder', {
                      pictures: photos?.length,
                    })}
                  />
                )}
              </>
            )}
          </FeedPostHeaderDataRow>
        </FeedPostHeaderStack>
        {isPostOwnedByUser && (
          <FeedPostButtonWrapper>
            {!isPinnable ? (
              <></>
            ) : (
              <FeedPostPin
                isPinned={pinned}
                onClickOnPin={() => onClickOnPin(id, pinned)}
              />
            )}
            <FeedPostMenu
              onClickOnEdit={!isEditable ? null : onClickOnEdit}
              onClickOnDelete={() => onClickOnDelete(id)}
            />
          </FeedPostButtonWrapper>
        )}
      </FeedPostHeader>
      <FeedPostBody
        bgColor={!!uploadProgressElement ? 'indigo.100' : 'gray.50'}
      >
        {status === PostStatusEnum.Broken ? (
          <FeedPostBrokenInlineAlert onDeletePost={() => onClickOnDelete(id)} />
        ) : (
          <AspectRatio
            ratio={4 / 5}
            maxH={600}
            /*
             * Workaround because the automatically generated before element
             * has a padding-bottom of 125% which blocks any element under it.
             */
            _before={{
              display: 'block',
              content: '""',
              height: 0,
              paddingBottom: '75%',
            }}
          >
            {uploadProgressElement ? (
              <Box>{uploadProgressElement}</Box>
            ) : (
              <Box>
                {children}
                {price && (
                  <FeedPostCenteredButtons>
                    <FeedPostBuyButton formattedPrice={formattedPrice} />
                  </FeedPostCenteredButtons>
                )}
              </Box>
            )}
          </AspectRatio>
        )}
      </FeedPostBody>
      <PostFooter
        postId={id}
        status={status}
        likes={likes}
        comments={comments.totalCount}
        tips={tips.count}
        price={price}
        isPublished={isPublished}
        publicationScheduledFor={publicationScheduledFor}
        purchaseCount={purchases?.count ?? 0}
        tippingGoal={tippingGoal}
        onClickOnLikes={onClickOnLikes}
        onClickOnTips={onClickOnTips}
        headline={title}
        text={text}
        borderRadius={isFeaturedPost ? '0 0 8px 8px' : '0'}
      />
    </FeedPost>
  );
};
